import { useCallback, useState } from 'react';
import {
  addMonths,
  eachMonthOfInterval,
  format,
  isSameMonth,
  startOfMonth,
  startOfYear,
} from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusiness } from '../contexts/BusinessContext';

export type RevenueWonByMonthRow = {
  monthKey: string;
  label: string;
  total: number;
  count: number;
  isCurrentMonth: boolean;
};

function monthKeyInTimezone(iso: string, timezone: string) {
  return format(toZonedTime(new Date(iso), timezone), 'yyyy-MM');
}

/**
 * Revenue won (Σ value of Won deals) per month, January through the current
 * month (business timezone). Won deals grouped by `updated_at` (close date),
 * matching the "Revenue won" KPI.
 */
export function useRevenueWonByMonthYtdBreakdown() {
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const timezone =
    selectedBusiness?.timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone ||
    'UTC';

  const [months, setMonths] = useState<RevenueWonByMonthRow[]>([]);
  const [yearTotal, setYearTotal] = useState(0);
  const [yearCount, setYearCount] = useState(0);
  const [yearLabel, setYearLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!supabase || !businessId) {
      setMonths([]);
      setYearTotal(0);
      setYearCount(0);
      setYearLabel('');
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const zonedNow = toZonedTime(new Date(), timezone);
      const yearStartZoned = startOfYear(zonedNow);
      const currentMonthZoned = startOfMonth(zonedNow);

      setYearLabel(format(zonedNow, 'yyyy'));

      const rangeStart = fromZonedTime(yearStartZoned, timezone).toISOString();
      const rangeEnd = fromZonedTime(
        startOfMonth(addMonths(zonedNow, 1)),
        timezone
      ).toISOString();

      const { data, error: queryError } = await supabase
        .from('pipeline_opportunities')
        .select('value, updated_at')
        .eq('business_id', businessId)
        .eq('status', 'Won')
        .gte('updated_at', rangeStart)
        .lt('updated_at', rangeEnd);

      if (queryError) throw queryError;

      const totalByMonth = new Map<string, number>();
      const countByMonth = new Map<string, number>();
      let yearSum = 0;
      let yearWonCount = 0;

      for (const deal of data ?? []) {
        if (!deal.updated_at) continue;
        const key = monthKeyInTimezone(deal.updated_at, timezone);
        const value = Number(deal.value) || 0;
        totalByMonth.set(key, (totalByMonth.get(key) ?? 0) + value);
        countByMonth.set(key, (countByMonth.get(key) ?? 0) + 1);
        yearSum += value;
        yearWonCount += 1;
      }

      const monthDates = eachMonthOfInterval({
        start: yearStartZoned,
        end: currentMonthZoned,
      });

      const rows: RevenueWonByMonthRow[] = monthDates.map((month) => {
        const key = format(month, 'yyyy-MM');
        return {
          monthKey: key,
          label: format(month, 'MMMM yyyy'),
          total: totalByMonth.get(key) ?? 0,
          count: countByMonth.get(key) ?? 0,
          isCurrentMonth: isSameMonth(month, zonedNow),
        };
      });

      setMonths(rows);
      setYearCount(yearWonCount);
      setYearTotal(yearSum);
    } catch (err) {
      console.error('Error loading revenue won by month YTD breakdown:', err);
      setMonths([]);
      setYearTotal(0);
      setYearCount(0);
      setError('Could not load revenue breakdown.');
    } finally {
      setLoading(false);
    }
  }, [supabase, businessId, timezone]);

  return { months, yearTotal, yearCount, yearLabel, loading, error, load };
}
